import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, Brain, Trophy, Plus, X, Loader2, Trash2, Play, GraduationCap } from 'lucide-react';
import HpDailyPass from './HpDailyPass';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- Lessons View ----
function LessonsView({ lessons }) {
  return (
    <div className="space-y-3" data-testid="memory-lessons">
      <section className="neo-card p-4 bg-gradient-to-br from-[#FFF7E6] via-[#FFF0F6] to-[#F5EEFF]">
        <p className="text-[10px] font-bold tracking-[0.25em] uppercase opacity-60">Jonas von Essen-skola</p>
        <h2 className="font-['Fredoka'] font-bold text-2xl mt-1">Minnesteknik 🧠</h2>
        <p className="text-sm opacity-70 mt-1">Hjärnan minns inte ord — den minns bilder. Lär dig konsten.</p>
      </section>
      {lessons.map((l) => (
        <section
          key={l.id}
          className="neo-card p-4 border-l-4"
          style={{ backgroundColor: `${l.color}55`, borderLeftColor: l.color }}
          data-testid={`memory-lesson-${l.id}`}
        >
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-2xl">{l.emoji}</span>
            <h3 className="font-['Fredoka'] font-bold text-lg">{l.n > 0 ? `${l.n}. ` : ''}{l.title}</h3>
          </div>
          <p className="text-sm font-bold opacity-80 mb-2">{l.summary}</p>
          <p className="text-sm whitespace-pre-line leading-relaxed">{l.body}</p>
          {l.tips && l.tips.length > 0 && (
            <ul className="mt-3 space-y-1 bg-white/60 rounded-md p-2">
              {l.tips.map((t, i) => (
                <li key={i} className="text-xs opacity-80">🌸 {t}</li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}

// ---- Practice View (words/numbers) ----
function PracticeView() {
  const [phase, setPhase] = useState('pick'); // pick | study | test | result
  const [kind, setKind] = useState('words');
  const [count, setCount] = useState(7);
  const [exercise, setExercise] = useState(null);
  const [studyTimer, setStudyTimer] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);

  const start = async () => {
    try {
      const res = await axios.get(`${API}/memory/exercises`, { params: { kind, count }, withCredentials: true });
      setExercise(res.data);
      setStudyTimer(res.data.study_time_sec);
      setAnswers(new Array(res.data.items.length).fill(''));
      setPhase('study');
    } catch { toast.error('Kunde inte starta'); }
  };

  useEffect(() => {
    if (phase !== 'study' || studyTimer <= 0) return;
    const t = setInterval(() => setStudyTimer((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [phase, studyTimer]);

  useEffect(() => {
    if (phase === 'study' && studyTimer === 0 && exercise) {
      setPhase('test');
    }
  }, [studyTimer, phase, exercise]);

  const submit = async () => {
    let score = 0;
    exercise.items.forEach((item, i) => {
      const expected = String(item).trim().toLowerCase();
      const given = String(answers[i] || '').trim().toLowerCase();
      if (expected === given) score += 1;
    });
    setResult({ score, total: exercise.items.length });
    try {
      await axios.post(`${API}/memory/attempts`, {
        exercise_kind: exercise.kind,
        score,
        total: exercise.items.length,
      }, { withCredentials: true });
    } catch { /* silent */ }
    setPhase('result');
  };

  if (phase === 'pick') {
    return (
      <div className="space-y-3" data-testid="memory-practice-pick">
        <section className="neo-card p-4 bg-white">
          <h3 className="font-['Fredoka'] font-bold text-lg mb-3">Välj övning</h3>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {[
              { id: 'words', emoji: '📝', label: 'Ord', desc: 'Memorera 7 ord' },
              { id: 'numbers', emoji: '🔢', label: 'Siffror', desc: 'Memorera 7 siffror' },
            ].map((opt) => (
              <button
                key={opt.id}
                onClick={() => setKind(opt.id)}
                className={`neo-btn p-3 text-left ${kind === opt.id ? 'bg-[#FFE8F5] ring-2 ring-[#D06892]' : 'bg-white'}`}
                data-testid={`memory-kind-${opt.id}`}
              >
                <p className="text-2xl mb-1">{opt.emoji}</p>
                <p className="font-bold text-sm">{opt.label}</p>
                <p className="text-[10px] opacity-60">{opt.desc}</p>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 mb-3">
            <label className="text-xs font-bold">Antal:</label>
            <input
              type="range"
              min={3}
              max={15}
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
              className="flex-1"
              data-testid="memory-count"
            />
            <span className="font-bold text-sm w-8 text-right">{count}</span>
          </div>
          <button onClick={start} className="neo-btn w-full py-3 bg-[#B9FBC0] font-bold flex items-center justify-center gap-1" data-testid="memory-start">
            <Play className="w-4 h-4" /> Starta
          </button>
        </section>
      </div>
    );
  }

  if (phase === 'study') {
    return (
      <div className="space-y-3" data-testid="memory-practice-study">
        <section className="neo-card p-4 bg-[#FFF8E1] text-center">
          <p className="text-xs font-bold uppercase opacity-60">Memorera nu</p>
          <p className="text-4xl font-bold font-['Fredoka'] my-2">{studyTimer}s</p>
          <div className="flex flex-wrap gap-2 justify-center my-4">
            {exercise.items.map((it, i) => (
              <span key={i} className="neo-card px-3 py-2 bg-white text-base font-bold">
                {String(it)}
              </span>
            ))}
          </div>
          <button onClick={() => setStudyTimer(0)} className="neo-btn px-4 py-2 bg-white text-sm font-bold" data-testid="memory-skip-study">
            Klar — testa mig
          </button>
        </section>
      </div>
    );
  }

  if (phase === 'test') {
    return (
      <div className="space-y-3" data-testid="memory-practice-test">
        <section className="neo-card p-4 bg-white">
          <p className="text-xs font-bold uppercase opacity-60 mb-2">Skriv vad du minns (i ordning)</p>
          <div className="space-y-2">
            {exercise.items.map((_, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs opacity-60 font-bold w-6">{i + 1}.</span>
                <input
                  type="text"
                  value={answers[i] || ''}
                  onChange={(e) => {
                    const next = [...answers];
                    next[i] = e.target.value;
                    setAnswers(next);
                  }}
                  className="neo-input flex-1 text-sm"
                  data-testid={`memory-answer-${i}`}
                />
              </div>
            ))}
          </div>
          <button onClick={submit} className="neo-btn w-full mt-3 py-3 bg-[#B9FBC0] font-bold" data-testid="memory-submit">
            Lämna in
          </button>
        </section>
      </div>
    );
  }

  // result
  const pct = result ? Math.round((result.score / result.total) * 100) : 0;
  return (
    <div className="space-y-3" data-testid="memory-practice-result">
      <section className="neo-card p-4 bg-gradient-to-br from-[#FFE8F5] to-[#F5EEFF] text-center">
        <p className="text-xs font-bold uppercase opacity-60">Resultat</p>
        <p className="text-5xl font-bold font-['Fredoka'] my-3">{result.score}/{result.total}</p>
        <p className="text-sm opacity-80">{pct}% rätt — {pct >= 80 ? 'Fantastiskt! 🌟' : pct >= 50 ? 'Bra jobbat 💗' : 'Försök igen — du blir bättre 🌸'}</p>
        <div className="grid grid-cols-2 gap-1 mt-3 text-left text-xs">
          {exercise.items.map((it, i) => {
            const correct = String(it).trim().toLowerCase() === String(answers[i] || '').trim().toLowerCase();
            return (
              <div key={i} className={`px-2 py-1 rounded ${correct ? 'bg-[#B9FBC0]' : 'bg-[#FFE8E8]'}`}>
                <span className="font-bold">{i + 1}.</span> {correct ? '✓' : '✗'} {String(it)}
              </div>
            );
          })}
        </div>
        <button onClick={() => { setPhase('pick'); setExercise(null); setResult(null); }} className="neo-btn w-full mt-3 py-3 bg-white font-bold" data-testid="memory-again">
          Träna igen
        </button>
      </section>
    </div>
  );
}

// ---- Högskoleprovet View ----
function HogskoleView() {
  const [parts, setParts] = useState([]);
  const [palaces, setPalaces] = useState([]);
  const [quizActive, setQuizActive] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [palaceMode, setPalaceMode] = useState(false);
  const [selectedPalace, setSelectedPalace] = useState(null);
  const [palaceWords, setPalaceWords] = useState([]);

  useEffect(() => {
    axios.get(`${API}/memory/hp/parts`, { withCredentials: true })
      .then((r) => setParts(r.data?.parts || []))
      .catch(() => {});
    axios.get(`${API}/memory/palaces`, { withCredentials: true })
      .then((r) => setPalaces(r.data?.palaces || []))
      .catch(() => {});
  }, []);

  const startOrdQuiz = async () => {
    try {
      const res = await axios.get(`${API}/memory/hp/ord-quiz`, { params: { count: 10 }, withCredentials: true });
      setQuestions(res.data?.questions || []);
      setQIdx(0);
      setAnswers([]);
      setShowResult(false);
      setQuizActive(true);
    } catch { toast.error('Kunde inte hämta quiz'); }
  };

  const startPalaceMode = async () => {
    if (palaces.length === 0) {
      toast.error('Skapa ett minnespalats först (Palats-fliken) 🌸');
      return;
    }
    try {
      const palace = palaces[0]; // default first palace
      const locCount = Math.min(palace.locations.length, 10);
      if (locCount === 0) {
        toast.error('Palatset saknar platser');
        return;
      }
      const res = await axios.get(`${API}/memory/hp/ord-quiz`, { params: { count: locCount }, withCredentials: true });
      setSelectedPalace(palace);
      setPalaceWords(res.data?.questions || []);
      setPalaceMode(true);
    } catch { toast.error('Kunde inte ladda'); }
  };

  if (palaceMode && selectedPalace) {
    return (
      <div className="space-y-3" data-testid="hp-palace-mode">
        <section className="neo-card p-4 bg-gradient-to-br from-[#A2D2FF]/40 to-[#FFE8F5]">
          <div className="flex items-baseline justify-between mb-2">
            <div>
              <p className="text-[10px] font-bold uppercase opacity-60">Loki-träning</p>
              <h3 className="font-['Fredoka'] font-bold text-lg">🏛️ {selectedPalace.name}</h3>
            </div>
            <button onClick={() => setPalaceMode(false)} className="neo-btn px-3 py-1 bg-white text-xs">← Tillbaka</button>
          </div>
          <p className="text-xs opacity-70 mb-3 italic">
            Gå igenom platserna i ditt palats och placera ett ord på varje. Föreställ dig en ABSURD scen där ordet och platsen möts. Ju knasigare, desto bättre minne.
          </p>
        </section>

        <div className="space-y-2" data-testid="hp-palace-pairs">
          {selectedPalace.locations.slice(0, palaceWords.length).map((loc, i) => {
            const q = palaceWords[i];
            if (!q) return null;
            return (
              <section
                key={i}
                className="neo-card p-3 bg-white border-l-4 border-[#A2D2FF]"
                data-testid={`hp-palace-pair-${i}`}
              >
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-xs font-bold opacity-50 w-6">{i + 1}.</span>
                  <span className="font-bold text-sm">📍 {loc}</span>
                  <span className="text-xs opacity-50">+</span>
                  <span className="font-['Fredoka'] font-bold text-base text-[#D06892]">{q.word}</span>
                </div>
                <div className="ml-8 mt-1 bg-[#FFF8E1] p-2 rounded text-xs">
                  <p className="font-bold opacity-70 mb-0.5">💡 Föreställ dig:</p>
                  <p className="italic">
                    En gigantisk, absurd scen där <b>{q.word}</b> händer på <b>{loc.toLowerCase()}</b>.
                    Lägg till färg, ljud, lukt, rörelse — desto knasigare desto bättre.
                  </p>
                  <p className="mt-1 text-[10px] opacity-60">Betydelse: {q.correct}</p>
                </div>
              </section>
            );
          })}
        </div>
        <button
          onClick={() => { setPalaceMode(false); startPalaceMode(); }}
          className="neo-btn w-full py-3 bg-[#B9FBC0] font-bold"
          data-testid="hp-palace-shuffle"
        >
          🔀 Nya ord (samma palats)
        </button>
      </div>
    );
  }

  if (quizActive) {
    if (showResult) {
      const score = answers.filter((a, i) => a === questions[i].correct).length;
      return (
        <div className="space-y-3">
          <section className="neo-card p-4 bg-gradient-to-br from-[#FFE8F5] to-[#F5EEFF] text-center" data-testid="hp-quiz-result">
            <p className="text-5xl font-bold my-2">{score}/{questions.length}</p>
            <p className="text-sm opacity-80 mb-3">{score >= 8 ? 'Topp! 🌟' : score >= 5 ? 'Bra start 💗' : 'Plugga vidare — det går!'}</p>
            <div className="space-y-1 text-left text-xs max-h-64 overflow-y-auto">
              {questions.map((q, i) => (
                <div key={i} className={`p-2 rounded ${answers[i] === q.correct ? 'bg-[#B9FBC0]' : 'bg-[#FFE8E8]'}`}>
                  <p className="font-bold">{q.word}</p>
                  <p className="text-[10px] opacity-70">Ditt svar: {answers[i] || '—'}</p>
                  <p className="text-[10px] font-bold">Rätt: {q.correct}</p>
                </div>
              ))}
            </div>
            <button onClick={() => setQuizActive(false)} className="neo-btn w-full mt-3 py-2 bg-white font-bold">Tillbaka</button>
          </section>
        </div>
      );
    }
    const q = questions[qIdx];
    if (!q) return null;
    return (
      <div className="space-y-3" data-testid="hp-quiz-active">
        <section className="neo-card p-4 bg-white">
          <p className="text-xs font-bold uppercase opacity-60">ORD-quiz · {qIdx + 1}/{questions.length}</p>
          <p className="font-['Fredoka'] font-bold text-2xl my-3">{q.word}</p>
          <p className="text-xs opacity-60 mb-2">Vad betyder det?</p>
          <div className="space-y-2">
            {q.options.map((opt, i) => (
              <button
                key={i}
                onClick={() => {
                  const next = [...answers, opt];
                  setAnswers(next);
                  if (qIdx + 1 >= questions.length) setShowResult(true);
                  else setQIdx(qIdx + 1);
                }}
                className="neo-btn w-full text-left px-3 py-2 bg-[#FFF8E1] hover:bg-[#FFE8A3] text-sm"
                data-testid={`hp-option-${i}`}
              >
                {opt}
              </button>
            ))}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="memory-hp">
      <HpDailyPass />
      <section className="neo-card p-4 bg-gradient-to-br from-[#FFE8F5] to-[#F5EEFF]">
        <p className="text-[10px] font-bold tracking-[0.25em] uppercase opacity-60">Högskoleprovet</p>
        <h2 className="font-['Fredoka'] font-bold text-2xl mt-1">HP-plugget 🎓</h2>
        <p className="text-sm opacity-70 mt-1">Lär dig provets 8 delar — och börja med ORD-quiz när du är redo.</p>
        <button onClick={startOrdQuiz} className="neo-btn mt-3 px-4 py-2 bg-[#B9FBC0] font-bold flex items-center gap-1" data-testid="hp-start-ord">
          <Play className="w-4 h-4" /> Starta ORD-quiz (10 frågor)
        </button>
        <button
          onClick={startPalaceMode}
          className="neo-btn mt-2 ml-2 px-4 py-2 bg-[#A2D2FF] font-bold flex items-center gap-1 inline-flex"
          data-testid="hp-start-palace"
          title={palaces.length === 0 ? 'Skapa ett palats först' : 'Placera HP-ord i ditt palats'}
        >
          🏛️ Placera ord i palats
        </button>
      </section>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {parts.map((p) => (
          <div
            key={p.id}
            className="neo-card p-3 border-l-4"
            style={{ backgroundColor: `${p.color}55`, borderLeftColor: p.color }}
            data-testid={`hp-part-${p.id}`}
          >
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-xl">{p.emoji}</span>
              <span className="font-['Fredoka'] font-bold text-base">{p.name}</span>
              <span className="text-[10px] opacity-60 ml-auto">{p.fullname}</span>
            </div>
            <p className="text-xs opacity-80 mb-2">{p.description}</p>
            <ul className="space-y-0.5 bg-white/60 rounded p-2">
              {p.tips.map((t, i) => <li key={i} className="text-[10px]">🌸 {t}</li>)}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Palaces View ----
function PalacesView() {
  const [palaces, setPalaces] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newLocs, setNewLocs] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/memory/palaces`, { withCredentials: true });
      setPalaces(r.data?.palaces || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!newName.trim()) return;
    const locs = newLocs.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    try {
      await axios.post(`${API}/memory/palaces`, { name: newName.trim(), locations: locs }, { withCredentials: true });
      setNewName(''); setNewLocs(''); setCreating(false);
      load();
      toast.success('Palats skapat ✨');
    } catch { toast.error('Kunde inte skapa'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Ta bort palatset?')) return;
    try { await axios.delete(`${API}/memory/palaces/${id}`, { withCredentials: true }); load(); }
    catch { toast.error('Kunde inte ta bort'); }
  };

  return (
    <div className="space-y-3" data-testid="memory-palaces">
      <section className="neo-card p-4 bg-gradient-to-br from-[#A2D2FF]/40 to-[#F5EEFF]">
        <p className="text-[10px] font-bold uppercase opacity-60">Loki-metoden</p>
        <h2 className="font-['Fredoka'] font-bold text-lg">Mina minnespalats 🏛️</h2>
        <p className="text-xs opacity-70 mt-1">Bygg palats med 5-10 fasta platser. Använd dem för att memorera listor.</p>
        {!creating && (
          <button onClick={() => setCreating(true)} className="neo-btn mt-3 px-3 py-1.5 bg-white text-sm font-bold flex items-center gap-1" data-testid="palace-add">
            <Plus className="w-4 h-4" /> Nytt palats
          </button>
        )}
      </section>
      {creating && (
        <section className="neo-card p-3 bg-white space-y-2" data-testid="palace-form">
          <input
            placeholder="Namn (t.ex. Mitt sovrum)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="neo-input w-full"
            data-testid="palace-name"
          />
          <textarea
            placeholder="Platser i ordning, en per rad eller komma-separerat&#10;Ex: dörr, garderob, säng, fönster..."
            value={newLocs}
            onChange={(e) => setNewLocs(e.target.value)}
            rows={4}
            className="neo-input w-full text-sm"
            data-testid="palace-locations"
          />
          <div className="flex gap-2">
            <button onClick={() => setCreating(false)} className="neo-btn flex-1 py-1.5 bg-gray-100 text-sm">Avbryt</button>
            <button onClick={create} className="neo-btn flex-1 py-1.5 bg-[#B9FBC0] text-sm font-bold" data-testid="palace-save">Spara</button>
          </div>
        </section>
      )}
      {palaces.length === 0 && !creating && (
        <p className="text-center text-xs opacity-60 italic py-6">Inga palats än 🌸</p>
      )}
      {palaces.map((p) => (
        <section key={p.id} className="neo-card p-3 bg-white" data-testid={`palace-${p.id}`}>
          <div className="flex items-baseline justify-between mb-2">
            <h4 className="font-['Fredoka'] font-bold">🏛️ {p.name}</h4>
            <button onClick={() => remove(p.id)} className="p-1 hover:bg-red-50 rounded">
              <Trash2 className="w-3.5 h-3.5 text-red-500" />
            </button>
          </div>
          <ol className="space-y-0.5 text-sm">
            {p.locations.map((loc, i) => (
              <li key={i} className="flex items-baseline gap-2">
                <span className="text-xs font-bold opacity-50 w-5">{i + 1}.</span>
                <span>{loc}</span>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}

// ---- Stats Dashboard ----
function StatsView() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/memory/stats`, { withCredentials: true })
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, []);
  if (!stats) return <p className="text-xs opacity-60 italic">Laddar...</p>;
  return (
    <section className="neo-card p-3 bg-[#FFF8E1]" data-testid="memory-stats">
      <h3 className="font-['Fredoka'] font-bold text-sm flex items-center gap-1 mb-2">
        <Trophy className="w-4 h-4" /> Din statistik (30 dagar)
      </h3>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div><p className="text-2xl font-bold">{stats.attempts_30d}</p><p className="text-[10px] opacity-60">Övningar</p></div>
        <div><p className="text-2xl font-bold">{Math.round((stats.avg_accuracy || 0) * 100)}%</p><p className="text-[10px] opacity-60">Snittpoäng</p></div>
        <div><p className="text-2xl font-bold">{stats.current_streak}🔥</p><p className="text-[10px] opacity-60">Streak</p></div>
      </div>
    </section>
  );
}

// ---- Main Tab ----
export default function MinnestraningTab() {
  const [subtab, setSubtab] = useState(() => {
    const s = sessionStorage.getItem('minnes-subtab');
    sessionStorage.removeItem('minnes-subtab');
    return s || 'lessons';
  });
  const [lessons, setLessons] = useState([]);

  useEffect(() => {
    axios.get(`${API}/memory/lessons`, { withCredentials: true })
      .then((r) => setLessons(r.data?.lessons || []))
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-3" data-testid="minnes-tab">
      <header className="neo-card p-4 bg-gradient-to-br from-[#FFE8F5] via-[#F5EEFF] to-[#E8F0FF]">
        <p className="text-[10px] font-bold tracking-[0.25em] uppercase opacity-60">Minnesträning</p>
        <h1 className="font-['Fredoka'] font-bold text-2xl mt-0.5">Lär dig minnas allt 🧠</h1>
      </header>

      <StatsView />

      <div className="flex gap-1.5 flex-wrap" data-testid="minnes-subtabs">
        {[
          { id: 'lessons', label: 'Lektioner', emoji: '📖', icon: BookOpen },
          { id: 'practice', label: 'Träna', emoji: '🎯', icon: Brain },
          { id: 'palaces', label: 'Palats', emoji: '🏛️', icon: BookOpen },
          { id: 'hp', label: 'Högskoleprovet', emoji: '🎓', icon: GraduationCap },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setSubtab(t.id)}
            className={`neo-btn px-3 py-2 text-sm font-bold flex items-center gap-1 ${subtab === t.id ? 'bg-[#FFC8DD]' : 'bg-white'}`}
            data-testid={`minnes-subtab-${t.id}`}
          >
            <span>{t.emoji}</span> {t.label}
          </button>
        ))}
      </div>

      {subtab === 'lessons' && <LessonsView lessons={lessons} />}
      {subtab === 'practice' && <PracticeView />}
      {subtab === 'palaces' && <PalacesView />}
      {subtab === 'hp' && <HogskoleView />}
    </div>
  );
}
